import {createSelector} from '@reduxjs/toolkit'

import {filter, find} from '@shared/helpers/collection'
import {pipe} from '@shared/helpers/function'
import {eq} from '@shared/helpers/lang'
import {maxBy} from '@shared/helpers/math'

import {Card, EasyCardItemType, InterestFreeInstallmentType, PayMethod} from '@web/core/constants'
import {
    CardInstallmentInfo,
    cardEasyPayInfoSelector,
    creditCardInfoSelector,
    getEasyCardInstallmentInfo,
    getCardInstallmentInfo,
    InterestFreeInstallment,
} from '@web/slices/card/slice'
import {payMethodInfoSelector} from '@web/slices/paymentMethod/slice'

import {paymentAmountInfoSelectors} from './paymentAmount'

const isSelectedEasyCard = eq(PayMethod.CARD_EASY_PAY)
const isSelectedGeneralCreditCard = eq(PayMethod.CARD)

type InstallmentInfo = {
    /** 선택한 카드의 할부 상세 정보 목록 */
    installments: CardInstallmentInfo[]
    /** 선택한 카드 할부 개월 */
    selectedInstallmentMonth: Maybe<string>
    /** 선택한 카드 할부 상세 정보 */
    selectedInstallmentInfo: Maybe<CardInstallmentInfo>
    /** 선택한 카드 할부 정보 (원본) */
    selectedInstallment: Maybe<InterestFreeInstallment>
    /** 선택한 카드할부의 부분 무이자 할부 적용 가능 회차 */
    partialInterestMonths: Maybe<CardInstallmentInfo['partialInterestMonths']>
}

const DefaultInstallmentInfo: InstallmentInfo = {
    installments: [],
    selectedInstallmentMonth: null,
    selectedInstallmentInfo: null,
    selectedInstallment: null,
    partialInterestMonths: null,
}

/** 간편카드 > 선택한 카드의 할부 정보 selector */
export const easyCardInstallmentInfoSelector = createSelector(
    paymentAmountInfoSelectors,
    cardEasyPayInfoSelector,
    ({usingPayMethodAmount: paymentAmount}, {selected: selectedEasyCard}): InstallmentInfo => {
        if (selectedEasyCard.info) {
            const {installments, easyCardInstallments} = getEasyCardInstallmentInfo({
                card: selectedEasyCard.info,
                interestFreeInstallments: selectedEasyCard.interestFreeInstallmentsByEasyCadeCode,
                paymentAmount,
            })

            const selectedEasyCardInstallmentInfo =
                find(({month}) => eq(`${month}`, selectedEasyCard.installment), easyCardInstallments) ?? null
            const selectedInstallment =
                find(({installmentMonth}) => eq(`${installmentMonth}`, selectedEasyCard.installment), installments) ??
                null

            return {
                installments: easyCardInstallments,
                selectedInstallmentMonth: selectedEasyCard.installment,
                selectedInstallmentInfo: selectedEasyCardInstallmentInfo,
                selectedInstallment,
                partialInterestMonths: selectedEasyCardInstallmentInfo?.partialInterestMonths ?? null,
            }
        }

        return DefaultInstallmentInfo
    },
)

/** 일반카드 할부 정보 selector */
export const cardInstallmentInfoSelector = createSelector(
    paymentAmountInfoSelectors,
    creditCardInfoSelector,
    ({usingPayMethodAmount: paymentAmount}, {selected: selectedCard}): InstallmentInfo => {
        if (selectedCard.companyCode) {
            const {installments, cardInstallments} = getCardInstallmentInfo({
                card: selectedCard,
                interestFreeInstallments: selectedCard.interestFreeInstallmentsByCadeCode,
                paymentAmount,
            })

            const selectedCardInstallmentInfo =
                find(({month}) => eq(`${month}`, selectedCard.installment), cardInstallments) ?? null
            const selectedInstallment =
                find(({installmentMonth}) => eq(`${installmentMonth}`, selectedCard.installment), installments) ?? null

            return {
                installments: cardInstallments,
                selectedInstallmentMonth: selectedCard.installment,
                selectedInstallmentInfo: selectedCardInstallmentInfo,
                selectedInstallment,
                partialInterestMonths: selectedCardInstallmentInfo?.partialInterestMonths ?? null,
            }
        }

        return DefaultInstallmentInfo
    },
)

/** 선택된 결제수단 카드의 할부 정보 selector */
export const selectedInstallmentInfoSelector = createSelector(
    payMethodInfoSelector,
    easyCardInstallmentInfoSelector,
    cardInstallmentInfoSelector,
    ({selectedPayMethod}, cardEasyPay, creditCard): InstallmentInfo => {
        if (isSelectedEasyCard(selectedPayMethod)) {
            return cardEasyPay
        }
        if (isSelectedGeneralCreditCard(selectedPayMethod)) {
            return creditCard
        }

        return DefaultInstallmentInfo
    },
)

/** 혜택 카드의 최대 무이자 할부 개월수 selector */
export const rewardCardMaxFreeInstallmentSelector = createSelector(
    paymentAmountInfoSelectors,
    cardEasyPayInfoSelector,
    ({usingPayMethodAmount: paymentAmount}, {cards, installmentsByCompanyCode}) => {
        return cards.reduce<Record<string, number>>((acc, card) => {
            const {interestFreeBenefitPromotion} = card
            const installments = installmentsByCompanyCode[card.companyCode] ?? []

            if (!interestFreeBenefitPromotion.isAvailable) {
                acc[card.id] = 0
                return acc
            }

            const {easyCardInstallments} = getEasyCardInstallmentInfo({
                card,
                interestFreeInstallments: installments,
                paymentAmount,
            })

            const isFreeInstallment = ({freeType}: CardInstallmentInfo) => freeType === InterestFreeInstallmentType.ALL
            const isMultiMonthInstallment = ({month}: CardInstallmentInfo) => month > 1
            const getMonth = ({month}: CardInstallmentInfo) => month

            const maxMonthInstallment = pipe(
                filter(isFreeInstallment),
                filter(isMultiMonthInstallment),
                maxBy(getMonth),
            )(easyCardInstallments)

            const isInterestFreeBenefitPromotionAvailable = interestFreeBenefitPromotion.month > 1
            const interestFreeBenefitPromotionMonth =
                paymentAmount >= interestFreeBenefitPromotion.minAmount ? interestFreeBenefitPromotion.month : 0
            const maxInterestFreeInstallmentMonth = maxMonthInstallment?.month || 0

            acc[card.id] = isInterestFreeBenefitPromotionAvailable
                ? interestFreeBenefitPromotionMonth
                : maxInterestFreeInstallmentMonth

            return acc
        }, {})
    },
)

/** 간편카드결제 > 선택된 카드 카드사 포인트 사용가능여부 Selector */
export const cardEasyPayCardCorpPointInfoSelector = createSelector(
    cardEasyPayInfoSelector,
    easyCardInstallmentInfoSelector,
    ({selected: selectedEasyCard, noCorpPointAvailableCard, corpPointSupported}, {installments}) => {
        const easyCardPlateSelected = selectedEasyCard && selectedEasyCard.itemType === EasyCardItemType.CARD
        const corpPointSupportedCardSelected = easyCardPlateSelected && corpPointSupported
        const installmentOptionFixed = selectedEasyCard.info?.type === Card.CREDIT && installments.length === 1
        /** 1개월 이상의 할부가 선택카드의 할부로 고정되어있다면 카드사 포인트 사용 불가. 일시불에서만 사용가능하기 때문 */
        const overOneMonthInstallmentFixed = installmentOptionFixed && installments[0] && installments[0].month !== 1
        /** nfc_prepaid 타입 카드는 카드사 포인트 사용 불가
         * @see https://oss.fin.navercorp.com/paynsp/qa/issues/2403#issuecomment-1113080
         */
        const nfcPrepaidCardSelected = selectedEasyCard.info?.type === Card.NFC_PREPAID
        const corpPointAvailable =
            selectedEasyCard.corpPointAvailable && !overOneMonthInstallmentFixed && !nfcPrepaidCardSelected

        return {
            /* 간편카드 중 카드사포인트 사용가능 카드 없음 */
            noCorpPointAvailableCard,
            /* 선택된 간편카드의 카드사포인트 존재 여부 */
            corpPointSupportedCardSelected,
            /* 선택된 간편카드의 카드사포인트 사용가능 여부 */
            corpPointAvailableCardSelected: corpPointAvailable,
        }
    },
)
